import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useInfiniteQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { FileText, Download, Search, Loader2, ExternalLink, RefreshCw, CalendarIcon, ArrowUp, ArrowDown, ArrowUpDown, X, Mail, GripVertical } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format, startOfDay, endOfDay, subDays, subMonths, subYears } from "date-fns";
import { parseDateOnly, formatDateOnly } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";

// Column configuration for resizable table
interface ColumnConfig {
  id: string;
  label: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  wrap: boolean;
}

const COLUMN_CONFIG: ColumnConfig[] = [
  { id: 'type', label: 'Type', defaultWidth: 110, minWidth: 80, maxWidth: 180, wrap: false },
  { id: 'authority', label: 'Authority', defaultWidth: 100, minWidth: 70, maxWidth: 160, wrap: false },
  { id: 'docket', label: 'Docket', defaultWidth: 110, minWidth: 90, maxWidth: 160, wrap: false },
  { id: 'company', label: 'Company', defaultWidth: 160, minWidth: 100, maxWidth: 300, wrap: true },
  { id: 'carrier', label: 'Carrier', defaultWidth: 100, minWidth: 80, maxWidth: 180, wrap: false },
  { id: 'serviceDate', label: 'Service Date', defaultWidth: 100, minWidth: 90, maxWidth: 140, wrap: false },
  { id: 'workOrder', label: 'Work Order', defaultWidth: 110, minWidth: 90, maxWidth: 160, wrap: false },
  { id: 'contact', label: 'Contact', defaultWidth: 120, minWidth: 80, maxWidth: 200, wrap: true },
  { id: 'emailStatus', label: 'Email Status', defaultWidth: 95, minWidth: 80, maxWidth: 140, wrap: false },
  { id: 'actions', label: 'Actions', defaultWidth: 90, minWidth: 70, maxWidth: 120, wrap: false },
];

const STORAGE_KEY = 'authority-letters-column-widths';

function getStoredColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {}
}

const getLetterTypeColor = (type: string | null) => {
  switch(type) {
    case 'Authority Grant': return 'bg-green-100 text-green-800 border-green-200';
    case 'Reinstatement': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Name Change': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'Transfer': return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'Mexican Carrier': return 'bg-orange-100 text-orange-800 border-orange-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getAuthorityTypeDescription = (code: string | null) => {
  if (!code) return null;
  const descriptions: Record<string, string> = {
    'A': 'Common Authority',
    'B': 'Contract Authority',
    'C': 'Broker Authority',
    'E': 'Freight Forwarder',
    'X': 'Cargo Tank',
  };
  return descriptions[code] || code;
};

const getEmailStatusBadge = (status: string | null) => {
  switch(status) {
    case 'scheduled':
      return { label: 'Scheduled', className: 'bg-blue-100 text-blue-700 border-blue-200' };
    case 'sent':
      return { label: 'Sent', className: 'bg-green-100 text-green-700 border-green-200' };
    case 'failed':
      return { label: 'Failed', className: 'bg-red-100 text-red-700 border-red-200' };
    case 'blocked_test_mode':
      return { label: 'Blocked (Test)', className: 'bg-amber-100 text-amber-700 border-amber-200' };
    default:
      return { label: 'Not Sent', className: 'bg-gray-100 text-gray-500 border-gray-200' };
  }
};

const LETTER_TYPES = [
  'Authority Grant',
  'Reinstatement',
  'Name Change',
  'Transfer',
  'Mexican Carrier',
];

type SortField = "serviceDate" | "letterType" | "docket" | "company";
type SortDirection = "asc" | "desc";

interface AuthorityLetterWithRelations {
  id: number;
  workOrderId: number;
  helixDocumentId: number;
  documentIdentifier: string;
  docketPrefix: string | null;
  docketNumber: number | null;
  docketSuffix: string | null;
  docketFull: string | null;
  usdotNumber: number | null;
  documentType: string | null;
  letterType: string | null;
  letterCode: string | null;
  authorityType: string | null;
  companyName: string | null;
  dbaName: string | null;
  cityState: string | null;
  serviceDate: string | null;
  requestDate: string | null;
  decidedDate: string | null;
  processedAt: string | null;
  downloadUrl: string | null;
  savedBy: string;
  createdAt: string;
  watchlistId: number | null;
  companyId: number | null;
  carrierId: number | null;
  emailStatus: string | null;
  emailSentAt: string | null;
  emailError: string | null;
  emailRetryCount: number | null;
  workOrder: { id: number; title: string; workOrderNumber: string } | null;
  company: { id: number; name: string; dotNumber: string | null } | null;
  carrier: { id: number; name: string; docketPrefix: string | null; docketNumber: string | null } | null;
  contact: { id: number; name: string; email: string | null } | null;
}

export default function AuthorityLettersPage() {
  usePageTitle("Authority Letters");
  const { toast } = useToast();
  const { user } = useAuth();
  const searchParams = useSearch();
  const [location, setLocation] = useLocation();
  
  const isInternalRole = user?.role === 'admin' || user?.role === 'staff';
  
  // Parse URL parameters
  const urlParams = new URLSearchParams(searchParams);
  const initialSearch = urlParams.get("search") || "";
  const initialWatchlistId = urlParams.get("watchlistId") || "";
  const initialLetterType = urlParams.get("letterType") || "";
  const initialDateRange = urlParams.get("dateRange") || "";
  const initialEmailStatus = urlParams.get("emailStatus") || "";
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [watchlistIdFilter, setWatchlistIdFilter] = useState(initialWatchlistId);
  const [letterTypeFilter, setLetterTypeFilter] = useState(initialLetterType);
  const [dateRangeFilter, setDateRangeFilter] = useState(initialDateRange);
  const [emailStatusFilter, setEmailStatusFilter] = useState(initialEmailStatus);

  // URL → state (reactive: fires when URL changes after mount, e.g. back/forward nav)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    setSearchQuery(params.get("search") || "");
    setWatchlistIdFilter(params.get("watchlistId") || "");
    setLetterTypeFilter(params.get("letterType") || "");
    setDateRangeFilter(params.get("dateRange") || "");
    setEmailStatusFilter(params.get("emailStatus") || "");
  }, [searchParams]);

  // state → URL (write back filter state so refresh/share preserves filters)
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("search", searchQuery.trim());
    if (watchlistIdFilter && watchlistIdFilter !== "all") params.set("watchlistId", watchlistIdFilter);
    if (letterTypeFilter && letterTypeFilter !== "all") params.set("letterType", letterTypeFilter);
    if (dateRangeFilter && dateRangeFilter !== "all") params.set("dateRange", dateRangeFilter);
    if (emailStatusFilter && emailStatusFilter !== "all") params.set("emailStatus", emailStatusFilter);
    const newSearch = params.toString();
    if (newSearch !== searchParams) {
      setLocation(`${location}${newSearch ? `?${newSearch}` : ''}`, { replace: true } as any);
    }
  }, [searchQuery, watchlistIdFilter, letterTypeFilter, dateRangeFilter, emailStatusFilter, searchParams, location, setLocation]);
  const [customDateFrom, setCustomDateFrom] = useState<Date | undefined>();
  const [customDateTo, setCustomDateTo] = useState<Date | undefined>();
  
  // Sort state
  const [sortField, setSortField] = useState<SortField>("serviceDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Column resize state
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const stored = getStoredColumnWidths();
    const defaults: Record<string, number> = {};
    COLUMN_CONFIG.forEach(col => {
      defaults[col.id] = stored[col.id] || col.defaultWidth;
    });
    return defaults;
  });

  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((columnId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const config = COLUMN_CONFIG.find(c => c.id === resizingRef.current!.columnId);
      if (!config) return;
      
      const diff = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(
        config.minWidth,
        Math.min(config.maxWidth, resizingRef.current.startWidth + diff)
      );
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current!.columnId]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(prev => {
          saveColumnWidths(prev);
          return prev;
        });
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const getColumnWidth = useCallback((columnId: string) => {
    return columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
  }, [columnWidths]);

  const resetColumnWidths = useCallback(() => {
    const defaults: Record<string, number> = {};
    COLUMN_CONFIG.forEach(col => {
      defaults[col.id] = col.defaultWidth;
    });
    setColumnWidths(defaults);
    saveColumnWidths(defaults);
  }, []);

  // Build query key based on filters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (searchQuery && searchQuery.trim()) params.set("search", searchQuery.trim());
    if (watchlistIdFilter && watchlistIdFilter !== "all") params.set("watchlistId", watchlistIdFilter);
    if (letterTypeFilter && letterTypeFilter !== "all") params.set("letterType", letterTypeFilter);
    if (emailStatusFilter && emailStatusFilter !== "all") params.set("emailStatus", emailStatusFilter);
    
    // Handle date range - skip if "all" 
    if (dateRangeFilter && dateRangeFilter !== "all") {
      const now = new Date();
      let fromDate: Date | undefined;
      let toDate: Date = endOfDay(now);
      
      switch (dateRangeFilter) {
        case "today":
          fromDate = startOfDay(now);
          break;
        case "7days":
          fromDate = startOfDay(subDays(now, 7));
          break;
        case "30days":
          fromDate = startOfDay(subDays(now, 30));
          break;
        case "90days":
          fromDate = startOfDay(subDays(now, 90));
          break;
        case "year":
          fromDate = startOfDay(subYears(now, 1));
          break;
        case "custom":
          if (customDateFrom) fromDate = startOfDay(customDateFrom);
          if (customDateTo) toDate = endOfDay(customDateTo);
          break;
      }
      
      if (fromDate) params.set("dateFrom", format(fromDate, "yyyy-MM-dd"));
      if (dateRangeFilter !== "custom" || customDateTo) {
        params.set("dateTo", format(toDate, "yyyy-MM-dd"));
      }
    }
    
    return params.toString();
  }, [searchQuery, watchlistIdFilter, letterTypeFilter, dateRangeFilter, customDateFrom, customDateTo]);

  const {
    data: lettersInfinite,
    isLoading,
    fetchNextPage: lettersFetchNextPage,
    hasNextPage: lettersHasNextPage,
    isFetchingNextPage: lettersIsFetchingNextPage,
  } = useInfiniteQuery<{ data: AuthorityLetterWithRelations[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ["/api/authority-letters", queryParams],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams(queryParams || '');
      if (pageParam) params.set('cursor', pageParam as string);
      const url = `/api/authority-letters${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch letters");
      return response.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  const lettersFlat = useMemo(() =>
    lettersInfinite?.pages.flatMap(p => p.data) ?? [],
    [lettersInfinite]
  );
  const lettersTotalCount = lettersInfinite?.pages[0]?.totalCount ?? lettersFlat.length;

  const backfillMutation = useMutation({
    mutationFn: async (letterId: number) => {
      return await apiRequest("POST", `/api/authority-letters/${letterId}/backfill`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/authority-letters"] });
      toast({
        title: "Success",
        description: "Authority letter metadata refreshed",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to refresh metadata",
        variant: "destructive",
      });
    },
  });

  const resendEmailMutation = useMutation({
    mutationFn: async (letterId: number) => {
      return await apiRequest("POST", `/api/authority-letters/${letterId}/resend-email`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/authority-letters"] });
      toast({
        title: "Email Sent",
        description: "Authority letter email has been sent",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Email Failed",
        description: error.message || "Failed to send email. Check settings for test mode restrictions.",
        variant: "destructive",
      });
    },
  });

  // Sort letters
  const sortedLetters = useMemo(() => {
    return [...lettersFlat].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case "serviceDate":
          const aDate = a.serviceDate ? parseDateOnly(a.serviceDate).getTime() : 0;
          const bDate = b.serviceDate ? parseDateOnly(b.serviceDate).getTime() : 0;
          comparison = aDate - bDate;
          break;
        case "letterType":
          comparison = (a.letterType || "").localeCompare(b.letterType || "");
          break;
        case "docket":
          comparison = (a.docketFull || "").localeCompare(b.docketFull || "");
          break;
        case "company":
          comparison = (a.companyName || "").localeCompare(b.companyName || "");
          break;
      }
      
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [lettersFlat, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleDownload = (letterId: number) => {
    window.open(`/api/authority-letters/${letterId}/download`, '_blank');
  };

  const clearFilters = () => {
    setSearchQuery("");
    setWatchlistIdFilter("");
    setLetterTypeFilter("");
    setDateRangeFilter("");
    setEmailStatusFilter("");
    setCustomDateFrom(undefined);
    setCustomDateTo(undefined);
    setLocation("/authority-letters");
  };

  const hasActiveFilters = searchQuery || watchlistIdFilter || letterTypeFilter || dateRangeFilter || emailStatusFilter;

  const SortButton = ({ field, label }: { field: SortField; label: string }) => {
    const isActive = sortField === field;
    const Icon = isActive ? (sortDirection === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn("h-8 px-2 -ml-2", isActive && "text-primary")}
        onClick={() => handleSort(field)}
      >
        {label}
        <Icon className="ml-1 h-3 w-3" />
      </Button>
    );
  };

  if (isLoading) {
    return (
      <ResponsiveLayout title="Authority Letters" subtitle="Search and manage FMCSA authority letters">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold tracking-tight">Authority Letters</h1>
          </div>
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout title="Authority Letters" subtitle="Search and manage FMCSA authority letters">
      <div className="space-y-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold tracking-tight">Authority Letters</h1>
            <div className="text-sm text-muted-foreground" aria-live="polite">
              {sortedLetters.length} of {lettersTotalCount} letter{lettersTotalCount !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap gap-3 items-center">
                {/* Search Input */}
                <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search docket, company, DOT..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="input-search-letters"
                  />
                </div>

                {/* Letter Type Filter */}
                <Select value={letterTypeFilter} onValueChange={setLetterTypeFilter}>
                  <SelectTrigger className="w-[160px] h-9" data-testid="select-letter-type">
                    <SelectValue placeholder="Letter Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {LETTER_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Email Status Filter */}
                <Select
                  value={emailStatusFilter || "all"}
                  onValueChange={(v) => setEmailStatusFilter(v === "all" ? "" : v)}
                >
                  <SelectTrigger className="w-[150px] h-9" data-testid="select-email-status">
                    <SelectValue placeholder="Email Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="unsent">Unsent</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="blocked_test">Blocked (Test)</SelectItem>
                  </SelectContent>
                </Select>

                {/* Date Range Filter */}
                <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                  <SelectTrigger className="w-[140px] h-9" data-testid="select-date-range">
                    <SelectValue placeholder="Date Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="7days">Last 7 Days</SelectItem>
                    <SelectItem value="30days">Last 30 Days</SelectItem>
                    <SelectItem value="90days">Last 90 Days</SelectItem>
                    <SelectItem value="year">Last Year</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>

                {/* Custom Date Range Pickers */}
                {dateRangeFilter === "custom" && (
                  <>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "h-9 justify-start text-left font-normal",
                            !customDateFrom && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {customDateFrom ? format(customDateFrom, "MMM d, yyyy") : "From"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={customDateFrom}
                          onSelect={setCustomDateFrom}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "h-9 justify-start text-left font-normal",
                            !customDateTo && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {customDateTo ? format(customDateTo, "MMM d, yyyy") : "To"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={customDateTo}
                          onSelect={setCustomDateTo}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </>
                )}

                {/* Watchlist ID indicator (when coming from watchlist) */}
                {watchlistIdFilter && (
                  <Badge variant="secondary" className="h-9 px-3 flex items-center gap-1">
                    Watchlist #{watchlistIdFilter}
                    <button
                      onClick={() => {
                        setWatchlistIdFilter("");
                        setLocation("/authority-letters");
                      }}
                      className="ml-1 hover:bg-muted rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-9"
                    data-testid="button-clear-filters"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Letters Table */}
          {sortedLetters.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="text-center space-y-3">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="text-lg font-medium">No authority letters found</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {hasActiveFilters
                      ? "Try adjusting your filters to see more results."
                      : "Authority letters from watchlist matches will appear here."}
                  </p>
                  {hasActiveFilters && (
                    <Button variant="outline" size="sm" onClick={clearFilters} data-testid="button-clear-filters-empty">
                      Clear filters
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <Table className="w-full" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    {COLUMN_CONFIG.map(col => (
                      <col key={col.id} style={{ width: `${getColumnWidth(col.id)}px` }} />
                    ))}
                  </colgroup>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="relative group" style={{ width: getColumnWidth('type') }}>
                        <div className="pr-2"><SortButton field="letterType" label="Type" /></div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('type', e)}
                          data-testid="resize-handle-type"
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('authority') }}>
                        <div className="pr-2">Authority</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('authority', e)}
                          data-testid="resize-handle-authority"
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('docket') }}>
                        <div className="pr-2"><SortButton field="docket" label="Docket" /></div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('docket', e)}
                          data-testid="resize-handle-docket"
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('company') }}>
                        <div className="pr-2">Company</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('company', e)}
                          data-testid="resize-handle-company"
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('carrier') }}>
                        <div className="pr-2">Carrier</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('carrier', e)}
                          data-testid="resize-handle-carrier"
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('serviceDate') }}>
                        <div className="pr-2"><SortButton field="serviceDate" label="Service Date" /></div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('serviceDate', e)}
                          data-testid="resize-handle-serviceDate"
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('workOrder') }}>
                        <div className="pr-2">Work Order</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('workOrder', e)}
                          data-testid="resize-handle-workOrder"
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('contact') }}>
                        <div className="pr-2">Contact</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('contact', e)}
                          data-testid="resize-handle-contact"
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('emailStatus') }}>
                        <div className="pr-2">Email Status</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('emailStatus', e)}
                          data-testid="resize-handle-emailStatus"
                        />
                      </TableHead>
                      <TableHead className="text-right" style={{ width: getColumnWidth('actions') }}>
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedLetters.map((letter) => (
                      <TableRow key={letter.id} data-testid={`row-letter-${letter.id}`}>
                        <TableCell className="whitespace-nowrap">
                          <Badge className={`${getLetterTypeColor(letter.letterType)} text-xs font-medium`}>
                            {letter.letterType || "Unknown"}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {letter.authorityType ? (
                            <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-2 py-0.5 rounded">
                              {getAuthorityTypeDescription(letter.authorityType)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="font-medium text-sm">
                            {letter.docketFull || `${letter.docketPrefix}-${letter.docketNumber}` || "-"}
                          </div>
                          {letter.usdotNumber && (
                            <div className="text-xs text-muted-foreground">
                              DOT: {letter.usdotNumber}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="break-words">
                          {letter.company ? (
                            <Link
                              href={`/companies/${letter.company.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-start gap-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`link-company-${letter.company.id}`}
                            >
                              <span className="break-words">{letter.company.name}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            </Link>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {letter.carrier ? (
                            <Link
                              href={`/carriers/${letter.carrier.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`link-carrier-${letter.carrier.id}`}
                            >
                              {letter.carrier.docketPrefix}-{letter.carrier.docketNumber}
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </Link>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {letter.serviceDate ? (
                            <span className="text-sm">
                              {formatDateOnly(letter.serviceDate, "MMM d, yyyy")}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {letter.workOrder ? (
                            <Link
                              href={`/work-orders/${letter.workOrder.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`link-work-order-${letter.workOrder.id}`}
                            >
                              {letter.workOrder.workOrderNumber || `#${letter.workOrder.id}`}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="break-words">
                          {letter.contact ? (
                            <Link
                              href={`/contacts/${letter.contact.id}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-start gap-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`link-contact-${letter.contact.id}`}
                              title={letter.contact.email || undefined}
                            >
                              <span className="break-words">{letter.contact.name}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            </Link>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {(() => {
                            const status = getEmailStatusBadge(letter.emailStatus);
                            return (
                              <Badge 
                                className={`${status.className} text-xs font-medium`}
                                title={letter.emailError || undefined}
                                data-testid={`badge-email-status-${letter.id}`}
                              >
                                {status.label}
                                {Number(letter.emailRetryCount) > 0 && (
                                  <span className="ml-1 text-xs opacity-70">({letter.emailRetryCount})</span>
                                )}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1">
                            {!letter.letterType && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => backfillMutation.mutate(letter.id)}
                                disabled={backfillMutation.isPending}
                                title="Refresh metadata from Helix API"
                                data-testid={`button-refresh-${letter.id}`}
                              >
                                <RefreshCw className={`w-4 h-4 ${backfillMutation.isPending ? 'animate-spin' : ''}`} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(letter.id)}
                              title="Download letter"
                              data-testid={`button-download-${letter.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {isInternalRole && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => resendEmailMutation.mutate(letter.id)}
                                disabled={resendEmailMutation.isPending || letter.emailStatus === 'blocked_test_mode'}
                                title={
                                  letter.emailStatus === 'blocked_test_mode'
                                    ? `Blocked by test mode - ${letter.contact?.email || 'contact email'} not in allowed domains`
                                    : letter.emailStatus === 'sent'
                                    ? 'Resend email notification'
                                    : 'Send email notification'
                                }
                                data-testid={`button-resend-email-${letter.id}`}
                              >
                                <Mail className={`w-4 h-4 ${resendEmailMutation.isPending ? 'opacity-50' : ''} ${letter.emailStatus === 'blocked_test_mode' ? 'opacity-30' : ''}`} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        <div className="flex flex-col items-center gap-2 mt-4 px-4 pb-4">
          <p className="text-sm text-muted-foreground" aria-live="polite">
            Showing {sortedLetters.length} of {lettersTotalCount}
          </p>
          {lettersHasNextPage && (
            <Button variant="outline" onClick={() => lettersFetchNextPage()} disabled={lettersIsFetchingNextPage}
              aria-label="Load more authority letters" aria-busy={lettersIsFetchingNextPage}>
              {lettersIsFetchingNextPage ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
              ) : "Load more"}
            </Button>
          )}
        </div>
        </div>
    </ResponsiveLayout>
  );
}
